import {
    IconPencilDollar,
    IconSitemap,
    IconUserCog,
    IconUserShare,
    IconTool,
    IconClipboardList,
    IconChalkboard,
    IconProps,
} from '@tabler/icons-react';
import React from 'react';

import { User } from '../codegen/graphql';

export type ModuleData = {
    value: string;
    description: string;
    Icon: (props: IconProps) => React.ReactNode;
    color: string;
    link: string;
};

export const MODULE_DASHBOARD_GRID_CLASS = 'w-[80rem] max-w-[calc(100vw-2rem)]';

export const MODULE_DATA: Record<string, ModuleData> = {
    'Price Change': {
        value: 'Price Change',
        description: 'View and action price changes',
        Icon: IconPencilDollar,
        color: 'blue',
        link: '/price-change',
    },
    'Stand-In Management': {
        value: 'Stand-In Management',
        description: 'Manage your stand-ins',
        Icon: IconUserShare,
        color: 'green',
        link: '/stand-in-management',
    },
    'User Management': {
        value: 'User Management',
        description: 'Manage portal users their roles',
        Icon: IconUserCog,
        color: 'violet',
        link: '/user-management',
    },
    'Organisation Hierarchy': {
        value: 'Organisation Hierarchy',
        description: 'View organisation hierarchy and update positions',
        Icon: IconSitemap,
        color: 'orange',
        link: '/organisation-hierarchy',
    },
    'Maintenance Tools': {
        value: 'Maintenance Tools',
        description: 'Sales Data Input, Top Product Changes and more',
        Icon: IconTool,
        color: 'red',
        link: '/maintenance-tools',
    },
    'Price Surveys': {
        value: 'Price Surveys',
        description: 'Manage Top 20, Categories and Special Project Surveys',
        Icon: IconClipboardList,
        color: 'blue',
        link: '/price-surveys',
    },
    Training: {
        value: 'Training',
        description: 'Help content and training materials',
        Icon: IconChalkboard,
        color: 'teal',
        link: '/training',
    },
};

const MODULES = [
    'Price Change',
    'Stand-In Management',
    'User Management',
    'Organisation Hierarchy',
    'Maintenance Tools',
    'Price Surveys',
    'Training',
];

const filterModules = (excludeModules: string[]): string[] => {
    return MODULES.filter(module => !excludeModules.includes(module));
};

const NORMAL_MODULES = filterModules(['User Management', 'Maintenance Tools']);

const ROLE_MODULES: Record<string, string[]> = {
    admin: MODULES,
    superadmin: MODULES,
    storemanager: ['Price Surveys', 'Price Change', 'Training'],
    user: NORMAL_MODULES,
    procurementmanager: NORMAL_MODULES,
    businessleader: NORMAL_MODULES,
    datastrategist: filterModules([
        'User Management',
        'Stand-In Management',
        'Price Change',
    ]),
    divisionalmanager: NORMAL_MODULES,
    opsexecutive: NORMAL_MODULES,
    auditor: filterModules(['User Management', 'Stand-In Management']),
};

const getRoleModules = (role: string) => {
    return ROLE_MODULES[role] || [];
};

export const PRICE_SURVEY_ZONE_BYPASS_ROLES = [
    'admin',
    'superadmin',
    'procurementmanager',
];

type PriceSurveyEnabledStore = {
    isClosed?: boolean | null;
    isPriceSurveyModuleEnabled?: boolean | null;
    advertisingZone?: {
        isPriceSurveyModuleEnabled?: boolean | null;
    } | null;
};

function isPriceSurveyEnabledForStore(
    store: PriceSurveyEnabledStore | null | undefined,
): boolean {
    if (!store || store.isClosed) {
        return false;
    }
    if (store.advertisingZone) {
        return store.advertisingZone.isPriceSurveyModuleEnabled === true;
    }
    return store.isPriceSurveyModuleEnabled === true;
}

export function getIsPriceSurveyModuleEnabled(
    user: User | null | undefined,
    role: string,
    virtualStores: PriceSurveyEnabledStore[] = [],
): boolean {
    if (!user) return false;
    if (PRICE_SURVEY_ZONE_BYPASS_ROLES.includes(role)) return true;
    if (role === 'storemanager') {
        return isPriceSurveyEnabledForStore(user.managerStore);
    }
    if (role === 'divisionalmanager') {
        const sapStoreEnabled =
            user.managerDivision?.some(
                division =>
                    division.stores?.some(store =>
                        isPriceSurveyEnabledForStore(store),
                    ),
            ) ?? false;
        if (sapStoreEnabled) {
            return true;
        }
        return virtualStores.some(store => isPriceSurveyEnabledForStore(store));
    }
    return false;
}

export function getIsPriceChangeModuleEnabled(
    user: User | null | undefined,
    isStoreManager: boolean,
): boolean {
    if (!user) return false;
    if (isStoreManager) {
        return user.managerStore?.isPriceChangeModuleEnabled ?? false;
    }
    return true;
}

export function getIsPricingBatchesModuleEnabled(
    user: User | null | undefined,
    role: string,
    pricingBatchesModuleEnabledFromConfig: boolean,
): boolean {
    if (!user || !pricingBatchesModuleEnabledFromConfig) {
        return false;
    }
    if (role === 'storemanager') {
        return false;
    }
    if (PRICE_SURVEY_ZONE_BYPASS_ROLES.includes(role)) {
        return true;
    }
    if (role === 'divisionalmanager') {
        return true;
    }
    return false;
}

export function getIsTrainingModuleEnabled(
    trainingModuleEnabledFromConfig: boolean,
): boolean {
    return trainingModuleEnabledFromConfig;
}

export { MODULES, getRoleModules };
